import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FiHome,
  FiCalendar,
  FiMapPin,
  FiTag,
  FiLayers,
  FiPackage,
  FiPercent,
  FiCreditCard,
  FiLogOut,
  FiCamera,
  FiMenu,
  FiX,
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import logoImage from '../assets/images/vgp-logo.png';
import './Layout.css';

const ALL_MENU_ITEMS = [
  { path: '/', label: 'Dashboard', icon: FiHome, roles: ['admin'] },
  { path: '/bookings', label: 'Bookings', icon: FiCalendar, roles: ['admin'] },
  { path: '/locations', label: 'Locations', icon: FiMapPin, roles: ['admin'] },
  { path: '/categories', label: 'Categories', icon: FiLayers, roles: ['admin'] },
  { path: '/tickets', label: 'Tickets', icon: FiTag, roles: ['admin'] },
  { path: '/addons', label: 'Add-ons', icon: FiPackage, roles: ['admin'] },
  { path: '/promocodes', label: 'Promo codes', icon: FiPercent, roles: ['admin'] },
  { path: '/payment-logs', label: 'Payment logs', icon: FiCreditCard, roles: ['admin'] },
  { path: '/scanner', label: 'Scanner', icon: FiCamera, roles: ['scanner'] },
];

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  /** Only scanner gets the scanner menu; any other role (including missing/unknown) sees full admin links. */
  const effectiveRole = user?.role === 'scanner' ? 'scanner' : 'admin';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    setMobileMenuOpen(false);
    logout();
    navigate('/login', { replace: true });
  };

  const menuItems = ALL_MENU_ITEMS.filter((item) => item.roles.includes(effectiveRole));
  const panelTitle = effectiveRole === 'scanner' ? 'Scanner' : 'Admin Panel';

  return (
    <div className="admin-layout">
      <header className="mobile-topbar">
        <button
          type="button"
          className="mobile-menu-toggle"
          aria-label="Open menu"
          aria-expanded={mobileMenuOpen}
          onClick={() => setMobileMenuOpen(true)}
        >
          <FiMenu aria-hidden />
        </button>
        <span className="mobile-topbar-title">{panelTitle}</span>
        <button type="button" className="mobile-logout" onClick={handleLogout} aria-label="Log out">
          <FiLogOut aria-hidden />
          <span className="mobile-logout-label">Logout</span>
        </button>
      </header>

      {mobileMenuOpen && (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Close menu"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <aside className={`sidebar ${mobileMenuOpen ? 'sidebar--open' : ''}`} id="admin-sidebar">
        <div className="sidebar-header">
          <button
            type="button"
            className="sidebar-close"
            aria-label="Close menu"
            onClick={() => setMobileMenuOpen(false)}
          >
            <FiX aria-hidden />
          </button>
          <img src={logoImage} alt="VGP Wonder World" className="sidebar-logo" />
          <h2>{panelTitle}</h2>
        </div>
        <nav className="sidebar-nav" aria-label="Main navigation">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const pathNorm = (location.pathname || '/').replace(/\/$/, '') || '/';
            const isActive = pathNorm === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Icon />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <button className="logout-button" onClick={handleLogout}>
          <FiLogOut />
          <span>Logout</span>
        </button>
      </aside>
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
